import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { getMyJobs, publishJob, pauseJob, closeJob, deleteJob } from '../../services/jobs';
import type { JobSummary, JobStatus } from '../../types/job';
import { JOB_TYPE_LABELS, LOCATION_TYPE_LABELS } from '../../types/job';

const STATUS_COLORS: Record<JobStatus, string> = {
  draft: 'bg-gray-100 text-gray-800',
  active: 'bg-green-100 text-green-800',
  paused: 'bg-yellow-100 text-yellow-800',
  closed: 'bg-red-100 text-red-800',
  filled: 'bg-blue-100 text-blue-800',
};

const STATUS_LABELS: Record<JobStatus, string> = {
  draft: 'Draft',
  active: 'Active',
  paused: 'Paused',
  closed: 'Closed',
  filled: 'Filled',
};

export function CompanyJobsPage() {
  const [jobs, setJobs] = useState<JobSummary[]>([]);
  const [filteredJobs, setFilteredJobs] = useState<JobSummary[]>([]);
  const [statusFilter, setStatusFilter] = useState<JobStatus | 'all'>('all');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [actionJobId, setActionJobId] = useState<string | null>(null);

  useEffect(() => {
    loadJobs();
  }, []);

  useEffect(() => {
    if (statusFilter === 'all') {
      setFilteredJobs(jobs);
    } else {
      // Note: JobSummary doesn't have status in current model, would need to add
      setFilteredJobs(jobs);
    }
  }, [jobs, statusFilter]);

  const loadJobs = async () => {
    setLoading(true);
    setError(null);

    try {
      const data = await getMyJobs();
      setJobs(data);
    } catch (err) {
      console.error('Failed to load jobs:', err);
      setError('Failed to load your jobs. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleAction = async (jobId: string, action: 'publish' | 'pause' | 'close' | 'delete') => {
    if (action === 'delete' && !confirm('Are you sure you want to delete this job?')) return;

    setActionJobId(jobId);

    try {
      switch (action) {
        case 'publish':
          await publishJob(jobId);
          break;
        case 'pause':
          await pauseJob(jobId);
          break;
        case 'close':
          await closeJob(jobId);
          break;
        case 'delete':
          await deleteJob(jobId);
          setJobs(prev => prev.filter(j => j.id !== jobId));
          setActionJobId(null);
          return;
      }
      await loadJobs(); // Reload to get updated status
    } catch (err: any) {
      console.error(`Failed to ${action} job:`, err);
      alert(err.response?.data?.error || `Failed to ${action} job. Please try again.`);
    } finally {
      setActionJobId(null);
    }
  };

  const formatSalary = (job: JobSummary) => {
    if (!job.show_salary || (!job.salary_min && !job.salary_max)) {
      return 'Competitive';
    }

    const currency = job.salary_currency || 'EUR';
    const formatter = new Intl.NumberFormat('en-IE', {
      style: 'currency',
      currency,
      maximumFractionDigits: 0,
    });

    if (job.salary_min && job.salary_max) {
      return `${formatter.format(job.salary_min)} - ${formatter.format(job.salary_max)}`;
    }
    if (job.salary_min) {
      return `From ${formatter.format(job.salary_min)}`;
    }
    if (job.salary_max) {
      return `Up to ${formatter.format(job.salary_max)}`;
    }
    return 'Competitive';
  };

  const formatDate = (dateStr?: string) => {
    if (!dateStr) return 'Not published';
    return new Date(dateStr).toLocaleDateString('en-IE', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  if (loading) {
    return (
      <div className="py-8">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between mb-8">
            <div>
              <div className="h-8 bg-gray-200 rounded w-40 mb-2 animate-pulse" />
              <div className="h-4 bg-gray-200 rounded w-64 animate-pulse" />
            </div>
            <div className="h-10 bg-gray-200 rounded w-32 animate-pulse" />
          </div>
          <div className="space-y-4">
            {[...Array(3)].map((_, i) => (
              <div
                key={i}
                className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 animate-pulse"
              >
                <div className="h-5 bg-gray-200 rounded w-1/2 mb-3" />
                <div className="h-4 bg-gray-200 rounded w-1/3" />
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="py-8">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Manage Jobs</h1>
            <p className="text-gray-600 mt-1">Create and manage your job postings</p>
          </div>
          <Link
            to="/company/jobs/new"
            className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-md font-medium hover:bg-blue-700"
          >
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 4v16m8-8H4"
              />
            </svg>
            Post New Job
          </Link>
        </div>

        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-md text-sm text-red-700">
            {error}
            <button onClick={loadJobs} className="ml-2 underline">
              Try again
            </button>
          </div>
        )}

        {/* Status filter */}
        <div className="mb-6 border-b border-gray-200">
          <nav className="flex gap-4 -mb-px">
            {(['all', 'draft', 'active', 'paused', 'closed', 'filled'] as const).map(status => (
              <button
                key={status}
                onClick={() => setStatusFilter(status)}
                className={`py-3 px-1 text-sm font-medium border-b-2 ${
                  statusFilter === status
                    ? 'border-blue-600 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                {status === 'all' ? 'All Jobs' : STATUS_LABELS[status]}
              </button>
            ))}
          </nav>
        </div>

        {/* Jobs list */}
        {filteredJobs.length === 0 ? (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center">
            <svg
              className="w-16 h-16 text-gray-400 mx-auto mb-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1.5}
                d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
              />
            </svg>
            <h3 className="text-lg font-medium text-gray-900 mb-2">No jobs yet</h3>
            <p className="text-gray-600 mb-6">Get started by posting your first job.</p>
            <Link
              to="/company/jobs/new"
              className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-md font-medium hover:bg-blue-700"
            >
              Post Your First Job
            </Link>
          </div>
        ) : (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Job
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Type
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Salary
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Published
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredJobs.map(job => (
                  <tr key={job.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div>
                        <Link
                          to={`/company/jobs/${job.id}`}
                          className="text-sm font-medium text-gray-900 hover:text-blue-600"
                        >
                          {job.title}
                        </Link>
                        <p className="text-xs text-gray-500 mt-1">
                          {job.category} · {LOCATION_TYPE_LABELS[job.location_type]}
                          {job.location_city && ` · ${job.location_city}`}
                        </p>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="text-sm text-gray-600">{JOB_TYPE_LABELS[job.job_type]}</span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="text-sm text-gray-600">{formatSalary(job)}</span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="text-sm text-gray-600">{formatDate(job.published_at)}</span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${STATUS_COLORS[job.is_featured ? 'active' : 'draft']}`}>
                        {job.is_featured ? 'Featured' : 'Standard'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm">
                      <div className="flex items-center justify-end gap-2">
                        <Link
                          to={`/company/jobs/${job.id}/applications`}
                          className="text-blue-600 hover:text-blue-800"
                        >
                          Applications
                        </Link>
                        <Link
                          to={`/company/jobs/${job.id}/edit`}
                          className="text-gray-600 hover:text-gray-800"
                        >
                          Edit
                        </Link>
                        <div className="relative inline-block text-left">
                          <button
                            className="text-gray-400 hover:text-gray-600"
                            onClick={e => {
                              e.stopPropagation();
                              const menu = e.currentTarget.nextElementSibling;
                              menu?.classList.toggle('hidden');
                            }}
                          >
                            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                              <path d="M10 6a2 2 0 110-4 2 2 0 010 4zM10 12a2 2 0 110-4 2 2 0 010 4zM10 18a2 2 0 110-4 2 2 0 010 4z" />
                            </svg>
                          </button>
                          <div className="hidden absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg ring-1 ring-black ring-opacity-5 z-10">
                            <div className="py-1">
                              <button
                                onClick={() => handleAction(job.id, 'publish')}
                                disabled={actionJobId === job.id}
                                className="block w-full px-4 py-2 text-sm text-left text-gray-700 hover:bg-gray-100"
                              >
                                Publish
                              </button>
                              <button
                                onClick={() => handleAction(job.id, 'pause')}
                                disabled={actionJobId === job.id}
                                className="block w-full px-4 py-2 text-sm text-left text-gray-700 hover:bg-gray-100"
                              >
                                Pause
                              </button>
                              <button
                                onClick={() => handleAction(job.id, 'close')}
                                disabled={actionJobId === job.id}
                                className="block w-full px-4 py-2 text-sm text-left text-gray-700 hover:bg-gray-100"
                              >
                                Close
                              </button>
                              <hr className="my-1" />
                              <button
                                onClick={() => handleAction(job.id, 'delete')}
                                disabled={actionJobId === job.id}
                                className="block w-full px-4 py-2 text-sm text-left text-red-600 hover:bg-red-50"
                              >
                                Delete
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
